<template>
  <div class="full-container">
    <div class="logo">
      <img class="py-4" src="@/assets/images/logot.svg" alt="" />
    </div>
    <div class="w-full flex flex-col items-center join-wrapper">
      <div class="bg-white w-full flex flex-row justify-between items-center px-4 h-8">
        <div class="bg-black w-4 h-4"></div>
        <div class="flex flex-row gap-2">
          <div class="bg-black w-4 h-4"></div>
          <div class="bg-black w-4 h-4"></div>
        </div>
      </div>
      <div class="join-body">
        <div class="join-body-left join-body-left-half">
          <p class="join-type">/* VC Gated Cast */</p>
          <p class="join-by">/* Hosted by {{ castDetails.event_creator_name }} */</p>
          <h2 class="room-name">{{ castDetails.event_name }}</h2>
          <div v-if="castDetails && castDetails.time">
            <CountDownTImer :castDetails="castDetails" />
          </div>

          <div>
            <div class="join-input-content">
              <!-- Credential verified - show join button -->
              <div v-if="credentialVerified" class="vc-section">
                <h3>Credential Verified</h3>
                <p>Access Level: {{ accessLevel }}</p>
                <button type="button" @click="joinCast" :disabled="isLoading">
                  <span>/Join as {{ displayName }}</span>
                </button>
              </div>

              <!-- No credential found -->
              <div v-else-if="did && !isLoading && verificationAttempted">
                <p class="error-msg">No valid credential found for this DID.</p>
                <p class="join-type">/* You need a valid Verifiable Credential to join this cast. Contact the host to get access. */</p>
              </div>

              <!-- DID not connected -->
              <div v-else-if="!did">
                <p class="join-type">/* This cast requires Verifiable Credential verification. Connect your DecastID to verify access. */</p>
              </div>

              <!-- Loading state -->
              <div v-else-if="isLoading">
                <p class="join-type">/* {{ loadingMessage }} */</p>
              </div>
            </div>

            <div class="join-body-bottom">
              <p v-if="did">>> did.address > <span>{{ displayName }}</span></p>
              <p v-else>>> did.status > <span>not connected</span></p>
              <p v-if="credentialVerified">>> credential.status > <span>verified</span></p>
              <p v-else-if="verificationAttempted">>> credential.status > <span>not found</span></p>
              <p v-if="walletType">>> wallet.type > <span>{{ walletType }}</span></p>
            </div>
          </div>
        </div>
        <div class="join-body-right flex flex-col justify-between">
          <div>
            <p class="join-type w-full">/* Connect your DecastID */</p>
            <div class="step-content">
              <button v-if="!did" @click="connectDidWallet" :disabled="isLoading" class="did-btn">
                <div class="did-icon-wrapper">
                  <DidIcon />
                </div>
                Connect DecastID
              </button>
              <button v-else class="did-btn connected">
                <div class="did-icon-wrapper">
                  <DidIcon />
                </div>
                {{ displayName }}
              </button>
              <p v-if="did" class="join-by mt-4">/* DecastID Connected */</p>

              <!-- Fallback to MetaMask if DID extension not available -->
              <div v-if="!hasDidExtension && !did" class="fallback-section">
                <p class="join-type mt-4">/* Or use MetaMask */</p>
                <button @click="connectMetaMask" :disabled="isLoading" class="metamask-btn">
                  <MetaMaskIcon />
                  Connect MetaMask
                </button>
              </div>
            </div>
          </div>
          <div>
            <div class="flex">
              <DidIcon class="mr-5 mb-5 large-icon" />
            </div>
            <p class="join-type w-full">/* Your DID will be used as your display name in the cast. */</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ethers } from 'ethers';
import CountDownTImer from './CountDownTImer.vue';
import { verifyCredential, getDIDFromAddress } from '@/services/credentialsService';
import DidIcon from '@/decast/icons/DidIcon.vue';
import constants from '@/../constant.js';

const EXTENSION_ID = constants.didManagerExtensionKey;

// Simple MetaMask icon component
const MetaMaskIcon = {
  template: `<svg class="metamask-svg" width="24" height="24" viewBox="0 0 37 35" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M35.9485 -0.000137329L20.1572 11.8073L23.0937 4.84884L35.9485 -0.000137329Z" fill="#E17726"/>
    <path d="M0.890501 0.0139465L13.7109 4.84984L16.4992 11.8995L0.890501 0.0139465ZM29.5943 25.1111L36.5738 25.2453L34.1346 33.619L25.618 31.2495L29.5943 25.1111ZM7.20833 25.1111L11.1698 31.2495L2.66762 33.6191L0.243164 25.2453L7.20833 25.1111Z" fill="#E27625"/>
  </svg>`
};

export default {
  name: 'JoinVcGatedCast',
  components: { CountDownTImer, DidIcon, MetaMaskIcon },
  props: ['castDetailsProps'],
  data() {
    return {
      meeting_id: this.$route.params.meetingID,
      castDetails: this.castDetailsProps || {},
      did: '',
      address: '',
      isLoading: false,
      loadingMessage: 'Connecting...',
      credentialVerified: false,
      verificationAttempted: false,
      accessLevel: '',
      permissions: [],
      hasDidExtension: false,
      walletType: '',
    };
  },
  computed: {
    displayName() {
      if (!this.did) return '';
      // Truncate DID for display: did:ethr:0x1234...5678 -> did:ethr:0x12...5678
      const parts = this.did.split(':');
      if (parts.length >= 3) {
        const address = parts[2];
        return `${parts[0]}:${parts[1]}:${address.slice(0, 4)}...${address.slice(-4)}`;
      }
      return this.did.length > 20 ? `${this.did.slice(0, 10)}...${this.did.slice(-6)}` : this.did;
    },
  },
  mounted() {
    document.getElementById('loading-bg').style.display = 'block';
    document.getElementById('loading-bg-transparent').style.display = 'none';

    // Check if DID extension is available
    this.checkDidExtension();

    // Listen for DID wallet messages
    window.addEventListener('message', this.handleDidSelected);

    if (this.castDetailsProps && this.castDetailsProps.public_meeting_id) {
      document.getElementById('loading-bg').style.display = 'none';
    } else {
      this.getMeetingDetails();
    }
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleDidSelected);
    window.removeEventListener('message', this.handleNonceSigned);
  },
  methods: {
    checkDidExtension() {
      if (window.chrome && chrome.runtime) {
        // Try to ping the extension
        chrome.runtime.sendMessage(
          EXTENSION_ID,
          { action: 'ping' },
          (response) => {
            if (!chrome.runtime.lastError) {
              this.hasDidExtension = true;
            }
          }
        );
      }
    },

    async connectDidWallet() {
      this.isLoading = true;
      this.loadingMessage = 'Opening DecastID wallet...';

      if (window.chrome && chrome.runtime) {
        chrome.runtime.sendMessage(
          EXTENSION_ID,
          { action: 'open-did-popup' },
          (response) => {
            if (chrome.runtime.lastError) {
              this.isLoading = false;
              this.$vs.notify({
                title: 'Extension Not Available',
                text: 'DecastID extension not installed. Redirecting to install...',
                color: 'warning',
              });
              window.open('https://chrome.google.com/webstore/detail/decast-did-manager/' + EXTENSION_ID, '_blank');
            }
          }
        );
      } else {
        this.isLoading = false;
        this.$vs.notify({
          title: 'Extension Not Available',
          text: 'Please install the DecastID extension or use MetaMask.',
          color: 'warning',
        });
      }
    },

    async handleDidSelected(event) {
      if (event.data?.action !== 'did-selected') return;

      window.removeEventListener('message', this.handleDidSelected);

      try {
        const { did } = event.data;
        this.did = typeof did === 'string' ? did : did?.did || '';
        this.walletType = 'DecastID';
        console.log("[VC-Gated] DID selected:", this.did);

        this.loadingMessage = 'Getting nonce for verification...';

        // Get nonce from auth backend
        const payload = { did: this.did };
        this.$store
          .dispatch('auth/v2getDidNonce', payload)
          .then((response) => {
            const { nonce, token } = response.data;
            this.nonceToken = token;

            this.loadingMessage = 'Please sign the nonce in your wallet...';

            // Send nonce to extension for signing
            chrome.runtime.sendMessage(
              EXTENSION_ID,
              { action: 'sign-nonce', nonce, did: this.did },
              (response) => {
                if (chrome.runtime.lastError) {
                  this.isLoading = false;
                  this.$vs.notify({
                    title: 'Error',
                    text: 'Error communicating with extension',
                    color: 'danger',
                  });
                }
              }
            );

            window.addEventListener('message', (e) => this.handleNonceSigned(e, token));
          })
          .catch((error) => {
            console.error('Error getting nonce:', error);
            this.isLoading = false;
            // Even if nonce fails, we can still try to verify credential
            this.verifyUserCredential();
          });
      } catch (err) {
        this.isLoading = false;
        this.$vs.notify({
          title: 'Error',
          text: err.message,
          color: 'danger',
        });
      }
    },

    async handleNonceSigned(event, token) {
      if (event.data?.action !== 'nonce-signed') return;

      window.removeEventListener('message', this.handleNonceSigned);

      if (event.data.error) {
        this.$vs.notify({
          title: 'Signing Canceled',
          text: event.data.error,
          color: 'warning',
        });
        this.isLoading = false;
        // Still try to verify credential without signature
        await this.verifyUserCredential();
        return;
      }

      const { signature } = event.data;
      console.log("[VC-Gated] Nonce signed:", signature);

      // Now verify the credential
      await this.verifyUserCredential();
    },

    async connectMetaMask() {
      try {
        this.isLoading = true;
        this.loadingMessage = 'Connecting MetaMask...';

        if (!window.ethereum) {
          this.$vs.notify({
            title: 'MetaMask Not Found',
            text: 'Please install MetaMask extension.',
            color: 'danger',
          });
          this.isLoading = false;
          return;
        }

        await window.ethereum.request({ method: 'eth_requestAccounts' });

        const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
        const signer = provider.getSigner();
        const currentAccount = await signer.getAddress();

        this.address = currentAccount.toString();
        this.did = getDIDFromAddress(this.address);
        this.walletType = 'MetaMask';

        // Verify credential
        await this.verifyUserCredential();
      } catch (error) {
        console.error('Error connecting MetaMask:', error);
        this.$vs.notify({
          title: 'Connection Failed',
          text: 'Failed to connect MetaMask. Please try again.',
          color: 'danger',
        });
        this.isLoading = false;
      }
    },

    async verifyUserCredential() {
      try {
        this.loadingMessage = 'Verifying your credential...';
        document.getElementById('loading-bg-transparent-title').innerText = 'Verifying your credential...';
        document.getElementById('loading-bg-transparent').style.display = 'flex';

        const result = await verifyCredential(this.did, this.meeting_id);

        this.verificationAttempted = true;

        if (result.valid) {
          this.credentialVerified = true;
          this.accessLevel = result.accessLevel || 'participant';
          this.permissions = result.permissions || [];
          this.$vs.notify({
            title: 'Credential Verified',
            text: 'You have access to this cast!',
            color: 'success',
          });
        } else {
          this.credentialVerified = false;
          this.$vs.notify({
            title: 'No Credential Found',
            text: 'You do not have a valid credential for this cast.',
            color: 'warning',
          });
        }
      } catch (error) {
        console.error('Error verifying credential:', error);
        this.verificationAttempted = true;
        this.credentialVerified = false;
        this.$vs.notify({
          title: 'Verification Failed',
          text: error.response?.data?.message || 'Failed to verify credential.',
          color: 'danger',
        });
      } finally {
        this.isLoading = false;
        document.getElementById('loading-bg-transparent').style.display = 'none';
      }
    },

    async getMeetingDetails() {
      const res = await this.$store.dispatch('cast/meetingInfo', this.meeting_id);
      const details = res.data.meeting_info;
      this.castDetails = details;
      document.getElementById('loading-bg').style.display = 'none';
    },

    async joinCast() {
      const payload = {
        name: this.displayName,
        email: '',
        password: '',
        public_meeting_id: this.meeting_id,
        avatar_url: '',
        isPublic: true,
      };

      document.getElementById('loading-bg-transparent-title').innerText = `Hang tight, ${this.displayName} Redirecting to the meeting`;
      document.getElementById('loading-bg-transparent').style.display = 'flex';

      this.$store
        .dispatch('studio/joinEvent', payload)
        .then((response) => {
          this.responsedata = response.data.url;
          window.location.href = response.data.url;
          this.$posthog.trackCastJoined(this.meeting_id);
          document.getElementById('loading-bg-transparent').style.display = 'none';
          this.$vs.notify({
            title: 'Success',
            text: response.data.message,
            color: 'success',
          });
        })
        .catch((e) => {
          console.log(e.response);
          document.getElementById('loading-bg-transparent').style.display = 'none';
          this.$vs.notify({
            title: 'Error Occurred',
            text: e.response?.data?.message || 'Failed to join the cast',
            color: 'danger',
          });
        });
    },
  },
};
</script>

<style scoped>
*:not(i) {
  font-family: 'JetBrains Mono' !important;
}

.vc-section h3 {
  color: #22C55E;
  margin-bottom: 10px;
}

.vc-section p {
  color: #5B96EB;
  margin-bottom: 20px;
}

.error-msg {
  color: #EF4444;
  margin-bottom: 10px;
}

.logo {
  height: 100px;
  display: flex;
  align-items: center;
}

.logo img {
  height: inherit;
}

.full-container {
  background-color: #000000;
  background-position: left center, right top;
  background-size: 30% 70%, contain;
  background-repeat: no-repeat, no-repeat;
  height: 100vh;
  width: 100%;
  padding-left: 67px;
  padding-right: 67px;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  padding-bottom: 50px;
}

.join-body {
  width: 100%;
  margin-top: 10px;
  display: flex;
}

.join-body-left {
  padding: 20px;
  border: 1px solid #FFFFFF;
  flex: 1;
}

.join-body-left-half {
  flex: 0.65 !important;
}

.join-body-right {
  padding: 20px;
  border: 1px solid #FFFFFF;
  flex: 0.35;
  margin-left: 10px;
}

.join-type {
  color: #5B96EB;
  width: 60%;
}

.join-by {
  color: #22C55E;
}

.join-input-content {
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  margin-bottom: 36px;
}

.join-input-content label {
  color: #FFFFFF;
}

.join-input-content button {
  background-color: #D7DF23;
  color: #000000;
  border: 2px solid #000000;
  outline: none;
  padding: 10px;
  width: fit-content;
  cursor: pointer;
  font-weight: 600;
  -webkit-box-shadow: 5px 5px 0px -1px rgba(255, 255, 255, 1);
  -moz-box-shadow: 5px 5px 0px -1px rgba(255, 255, 255, 1);
  box-shadow: 5px 5px 0px -1px rgba(255, 255, 255, 1);
}

.join-input-content button:disabled {
  opacity: 0.5;
}

.join-input-content button span {
  margin-right: 30px;
}

.step-content button {
  margin: 14px 0px;
  background-color: #D7DF23;
  color: #000000;
  border: 2px solid #000000;
  outline: none;
  padding: 10px 16px;
  width: fit-content;
  cursor: pointer;
  font-weight: 600;
  -webkit-box-shadow: 5px 5px 0px -1px rgba(255, 255, 255, 1);
  -moz-box-shadow: 5px 5px 0px -1px rgba(255, 255, 255, 1);
  box-shadow: 5px 5px 0px -1px rgba(255, 255, 255, 1);
  display: flex;
  align-items: center;
  gap: 10px;
}

.step-content button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.did-btn {
  background-color: #D3CA57 !important;
}

.did-btn.connected {
  background-color: #22C55E !important;
  color: #000000 !important;
}

.did-icon-wrapper {
  background-color: #1B1D29;
  border: 1px solid #242632;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
}

.did-icon-wrapper svg {
  height: 16px;
  width: 16px;
}

.metamask-btn {
  background-color: #F6851B !important;
  margin-top: 10px;
}

.metamask-btn svg {
  height: 20px;
  width: 20px;
}

.fallback-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #333;
}

.large-icon {
  opacity: 0.4;
}

.large-icon svg {
  height: 35px;
  width: 37px;
}

.room-name {
  color: #FFFFFF;
  font-weight: 700;
  max-width: 450px;
  margin-top: 10px;
  margin-bottom: 10px;
}

.join-body-bottom {
  margin-top: 5px;
  color: #FFFFFF;
}

.join-body-bottom span {
  color: #22C55E;
}

@media (max-width: 499px) {
  .full-container {
    background-image: none;
    background-position: right center;
    background-size: contain;
    background-repeat: no-repeat;
    height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    padding-left: 10px;
    padding-right: 10px;
    overflow-y: scroll;
    padding-bottom: 50px;
  }
}
</style>
